import { fieldErrorSig, type TabFieldMap } from '../tabErrors.js';

/**
 * Feldkataloge fuer Formular D (Zementartenstatistik), aus `FormularDForm.tsx` ausgelagert
 * (Plan 23-02 Task 1). Werte, Reihenfolge und Schreibweise sind gegenueber dem vorherigen Stand
 * in `FormularDForm.tsx` UNVERAENDERT -- der Umbau der Darstellung passiert erst in 23-03/23-06.
 *
 * Alle Ableitungen unten (CEM_GROUP_FIELD_MAP, FORMULAR_D_CALC_INPUT_KEYS,
 * FORMULAR_D_CALC_OUTPUT_KEYS, SECTION2_GROUPS) sind aus denselben Katalogen berechnet -- keine
 * handgepflegte zweite Liste.
 */

// Strength class configuration
export const STRENGTH_CLASSES = ['32_5_L', '32_5_N', '32_5_R', '42_5_L', '42_5_N', '42_5_R', '52_5_L', '52_5_N', '52_5_R'] as const;
export const STRENGTH_LABELS: Record<string, string> = {
  '32_5_L': '32,5 L',
  '32_5_N': '32,5 N',
  '32_5_R': '32,5 R',
  '42_5_L': '42,5 L',
  '42_5_N': '42,5 N',
  '42_5_R': '42,5 R',
  '52_5_L': '52,5 L',
  '52_5_N': '52,5 N',
  '52_5_R': '52,5 R',
};

/**
 * Cement types grouped by CEM category.
 *
 * Die `label`-Werte tragen seit Plan 23-09 den Klartextnamen, wie ihn der Figma-Screen
 * `design/final/05 Melder_ Marktdaten.jpg` zeigt („CEM I – Portlandzement"). Vorher stand dort
 * nur „CEM I"; die Nutzerabnahme hat das als Abweichung vom Design beanstandet.
 *
 * Eine Ausnahme: der Figma-Screen wiederholt bei der zweiten Gruppe „CEM I – Portlandzement",
 * obwohl darunter die zwanzig CEM-II-Arten stehen — erkennbar ein Copy-Paste-Fehler im Design.
 * Dort steht deshalb der Normbegriff nach DIN EN 197 („Portlandkompositzement") statt einer
 * uebernommenen Fehlbezeichnung.
 */
export const CEMENT_GROUPS = [
  {
    id: 'cemI',
    label: 'CEM I – Portlandzement',
    description: 'Portlandzement',
    color: '#1976d2',
    types: [
      { prefix: 'cemI', label: 'CEM I', shortLabel: 'CEM I' },
    ],
  },
  {
    id: 'cemII',
    label: 'CEM II – Portlandkompositzement',
    description: 'Portlandkompositzemente',
    color: '#388e3c',
    types: [
      { prefix: 'cemII_A_S', label: 'Portlandhüttenzement CEM II/A-S', shortLabel: 'II/A-S' },
      { prefix: 'cemII_B_S', label: 'Portlandhüttenzement CEM II/B-S', shortLabel: 'II/B-S' },
      { prefix: 'cemII_A_D', label: 'Portlandsilicastaubzement CEM II/A-D', shortLabel: 'II/A-D' },
      { prefix: 'cemII_A_P', label: 'Portlandpuzzolanzement CEM II/A-P', shortLabel: 'II/A-P' },
      { prefix: 'cemII_B_P', label: 'Portlandpuzzolanzement CEM II/B-P', shortLabel: 'II/B-P' },
      { prefix: 'cemII_A_Q', label: 'Portlandpuzzolanzement CEM II/A-Q', shortLabel: 'II/A-Q' },
      { prefix: 'cemII_B_Q', label: 'Portlandpuzzolanzement CEM II/B-Q', shortLabel: 'II/B-Q' },
      { prefix: 'cemII_A_V', label: 'Portlandflugaschezement CEM II/A-V', shortLabel: 'II/A-V' },
      { prefix: 'cemII_B_V', label: 'Portlandflugaschezement CEM II/B-V', shortLabel: 'II/B-V' },
      { prefix: 'cemII_A_W', label: 'Portlandflugaschezement CEM II/A-W', shortLabel: 'II/A-W' },
      { prefix: 'cemII_B_W', label: 'Portlandflugaschezement CEM II/B-W', shortLabel: 'II/B-W' },
      { prefix: 'cemII_A_T', label: 'Portlandölschieferzement CEM II/A-T', shortLabel: 'II/A-T' },
      { prefix: 'cemII_B_T', label: 'Portlandölschieferzement CEM II/B-T', shortLabel: 'II/B-T' },
      { prefix: 'cemII_A_L', label: 'Portlandkalksteinzement CEM II/A-L', shortLabel: 'II/A-L' },
      { prefix: 'cemII_B_L', label: 'Portlandkalksteinzement CEM II/B-L', shortLabel: 'II/B-L' },
      { prefix: 'cemII_A_LL', label: 'Portlandkalksteinzement CEM II/A-LL', shortLabel: 'II/A-LL' },
      { prefix: 'cemII_B_LL', label: 'Portlandkalksteinzement CEM II/B-LL', shortLabel: 'II/B-LL' },
      { prefix: 'cemII_A_M', label: 'Portlandkompositzement CEM II/A-M', shortLabel: 'II/A-M' },
      { prefix: 'cemII_B_M', label: 'Portlandkompositzement CEM II/B-M', shortLabel: 'II/B-M' },
      { prefix: 'cemII_C_M', label: 'Portlandkompositzement CEM II/C-M', shortLabel: 'II/C-M' },
    ],
  },
  {
    id: 'cemIII',
    label: 'CEM III – Hochofenzement',
    description: 'Hochofenzement',
    color: '#7b1fa2',
    types: [
      { prefix: 'cemIII_A', label: 'Hochofenzement CEM III/A', shortLabel: 'III/A' },
      { prefix: 'cemIII_B', label: 'Hochofenzement CEM III/B', shortLabel: 'III/B' },
      { prefix: 'cemIII_C', label: 'Hochofenzement CEM III/C', shortLabel: 'III/C' },
    ],
  },
  {
    id: 'cemIV',
    label: 'CEM IV – Puzzolanzement',
    description: 'Puzzolanzement',
    color: '#f57c00',
    types: [
      { prefix: 'cemIV_A', label: 'Puzzolanzement CEM IV/A', shortLabel: 'IV/A' },
      { prefix: 'cemIV_B', label: 'Puzzolanzement CEM IV/B', shortLabel: 'IV/B' },
    ],
  },
  {
    id: 'cemV',
    label: 'CEM V – Kompositzement',
    description: 'Kompositzement',
    color: '#c62828',
    types: [
      { prefix: 'cemV_A', label: 'Kompositzement CEM V/A', shortLabel: 'V/A' },
      { prefix: 'cemV_B', label: 'Kompositzement CEM V/B', shortLabel: 'V/B' },
    ],
  },
  {
    id: 'cemVI',
    label: 'CEM VI – Zement CEM VI',
    description: 'Zement CEM VI',
    color: '#455a64',
    types: [
      { prefix: 'cemVI', label: 'Zement CEM VI', shortLabel: 'VI' },
    ],
  },
];

// Section II field configs (module scope: statisch, gebraucht von den JSX-Labels UND von
// calculateGrandTotal fuer die Section-2-Teilsummen).
export const section2aFields = [
  { key: 'zugelassene_32_5_N', label: '32,5 N' },
  { key: 'zugelassene_32_5_R', label: '32,5 R' },
  { key: 'zugelassene_42_5_N', label: '42,5 N' },
  { key: 'zugelassene_42_5_R', label: '42,5 R' },
  { key: 'zugelassene_52_5_N', label: '52,5 N' },
  { key: 'zugelassene_52_5_R', label: '52,5 R' },
];

export const section2bFields = [
  { key: 'putz_5_0', label: '5,0' },
  { key: 'putz_12_5', label: '12,5' },
  { key: 'putz_12_5_X', label: '12,5 X' },
  { key: 'putz_22_5', label: '22,5' },
  { key: 'putz_22_5_X', label: '22,5 X' },
];

export const section2cFields = [
  { key: 'tragschicht_E2', label: 'E 2' },
  { key: 'tragschicht_E3', label: 'E 3' },
  { key: 'tragschicht_E4', label: 'E 4' },
  { key: 'tragschicht_E4_RS', label: 'E4-RS' },
];

// Single Source of Truth fuer die 278 schreibbaren FormularD-Feld-Keys (18-08 Gate-Fix): aus
// CEMENT_GROUPS x STRENGTH_CLASSES + den drei Section-II-Feldlisten + den drei Einzelfeldern
// abgeleitet -- denselben Katalogen, die auch die Felder rendern. hydrate.ts importiert
// genau diese Liste, um beim Hydrieren AUSSCHLIESSLICH diese Keys (+ notes) aus der Entity in den
// Form-State zu uebernehmen. Ein Blacklist-Ansatz (heutige Metadaten-Keys herausfiltern) wuerde
// bei jedem neuen Entity-Feld erneut lautlos brechen -- diese Whitelist kann dagegen nie von der
// Form abweichen, weil sie aus denselben Katalogen kompiliert wird, die die Felder rendern.
export const FORMULAR_D_FIELD_KEYS: string[] = [
  ...CEMENT_GROUPS.flatMap((group) =>
    group.types.flatMap((type) => STRENGTH_CLASSES.map((suffix) => `${type.prefix}_${suffix}`)),
  ),
  ...section2aFields.map((f) => f.key),
  ...section2bFields.map((f) => f.key),
  ...section2cFields.map((f) => f.key),
  'klinkermehl',
  'beitragspflichtig',
];

/**
 * Gruppen-Id -> alle Feldnamen der Gruppe (D-14: Traeger des Fehlerpunkts an den zugeklappten
 * CEM-Gruppen). Ausgewertet mit dem bestehenden `tabsWithErrors` -- keine neue
 * Aggregationsfunktion.
 */
export const CEM_GROUP_FIELD_MAP: TabFieldMap = Object.fromEntries(
  CEMENT_GROUPS.map((group) => [
    group.id,
    group.types.flatMap((type) => STRENGTH_CLASSES.map((suffix) => `${type.prefix}_${suffix}`)),
  ]),
);

/**
 * Fehler-Signatur einer CEM-Gruppe (D-04, Comparator fuer die `memo()`-gewrappte
 * `CementGroupCard`). Leitet ihre Feldpfade AUSSCHLIESSLICH aus `CEM_GROUP_FIELD_MAP` ab -- es
 * entsteht keine zweite Feldliste. Summenpfade gehoeren ausdruecklich NICHT hinein: die
 * Backend-Entity `FormularD` hat keine Summenspalten, die Zeilensumme ist schlichter `Text` ohne
 * Fehlerdarstellung.
 *
 * Faellt beim Ableiten ein Feldpfad aus `CEM_GROUP_FIELD_MAP` heraus, bleibt ein vom Backend
 * abgelehnter Wert auf genau diesem Feld unsichtbar, weil die memoisierte Karte nicht neu
 * rendert (T-19-27/T-19-28) -- deshalb der 261-Felder-Vollstaendigkeitstest in `catalog.test.ts`.
 */
export function cemGroupErrorSig(errors: Record<string, unknown>, groupId: string): string {
  return fieldErrorSig(errors, CEM_GROUP_FIELD_MAP[groupId] ?? []);
}

/**
 * FORMULAR_D_FIELD_KEYS ohne die Calculator-AUSGABE `beitragspflichtig` -- die 277
 * rechenrelevanten Eingabefelder, identisch zu `FormularDFieldCatalog::calculationFieldNames()`
 * (Backend, Plan 23-01).
 */
export const FORMULAR_D_CALC_INPUT_KEYS: string[] = FORMULAR_D_FIELD_KEYS.filter(
  (key) => key !== 'beitragspflichtig',
);

/**
 * Die 41 Schluessel, die `POST /api/reports/calculate` fuer `type: "formular_d"` zurueckgibt
 * (`FormularDCalculator::calculate()`, Plan 23-01): 29 Zeilensummen je Zementart, 6
 * Gruppensummen, dann die sechs festen Schluessel.
 */
export const FORMULAR_D_CALC_OUTPUT_KEYS: string[] = [
  ...CEMENT_GROUPS.flatMap((group) => group.types.map((type) => `summe_${type.prefix}`)),
  ...CEMENT_GROUPS.map((group) => `summe_gruppe_${group.id}`),
  'summe_normzement',
  'summe_zugelassene',
  'summe_putz',
  'summe_tragschicht',
  'gesamtmenge',
  'beitragspflichtig',
];

/** Die drei Abschnitt-II-Unterabschnitte, Titel wortgleich aus dem heutigen JSX uebernommen. */
/*
 * `note` traegt den Text, der frueher als Fussnote ¹⁾/²⁾ am Seitenende stand. Der Verweis
 * ist entfallen: die Marke stand am Blocktitel, der Text nach dem Summenbalken ganz unten --
 * in 10px Grau, hinter dem auffaelligsten Element der Seite. Wer die Marke sah, musste
 * springen; wer nicht sprang, las den Hinweis nie. Da jeder Block seit der Umstellung auf
 * Karten einen eigenen Kopf hat, steht der Hinweis jetzt dort, wo er gilt. Dass der Text zu
 * II.b und II.c derselbe ist und damit zweimal erscheint, ist der Preis dafuer -- und
 * billiger als ein Verweis, dem niemand folgt.
 */
export const SECTION2_GROUPS = [
  {
    id: 'zugelassene',
    title: 'II.a Bauaufsichtlich zugelassene Zemente',
    note: 'Gesamtmenge des im Inland versandten Zements.',
    fields: section2aFields,
    serverKey: 'summe_zugelassene',
  },
  {
    id: 'putz',
    title: 'II.b Putz- und Mauerbinder EN 413-1',
    note:
      'Hier sind die Gesamtmengen des im Inland versandten Zements einzutragen. Darin enthaltene '
      + 'Zementmengen und etwaiges Klinkermehl sind zusätzlich in den entsprechenden Zeilen unter '
      + 'Abschnitt I. bzw. III. aufzulisten.',
    fields: section2bFields,
    serverKey: 'summe_putz',
  },
  {
    id: 'tragschicht',
    title: 'II.c Hydraulische Tragschichtbinder EN 13282',
    note:
      'Hier sind die Gesamtmengen des im Inland versandten Zements einzutragen. Darin enthaltene '
      + 'Zementmengen und etwaiges Klinkermehl sind zusätzlich in den entsprechenden Zeilen unter '
      + 'Abschnitt I. bzw. III. aufzulisten.',
    fields: section2cFields,
    serverKey: 'summe_tragschicht',
  },
] as const;
